export const normalizeVariantValue = (value = "") => String(value)
  .trim()
  .toLowerCase()
  .replace(/\s+/g, " ")
  .replace(/[-_]/g, " ");

const normalizeComparable = (value = "") => normalizeVariantValue(value)
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const variantTokens = (variant = {}) => normalizeComparable(
  `${variant.sku || ""} ${variant.name || variant.label || ""}`
).split(/\s+/).filter(Boolean);

const structuredVariantValues = (variant = {}) => [
  variant.selectedFilters,
  variant.filters,
  variant.filter,
  variant.attributes,
].filter((entry) => entry && typeof entry === "object" && !Array.isArray(entry));

const matchesSelectedFilterValue = (variant, key, rawValue) => {
  const wanted = normalizeComparable(rawValue);
  if (!wanted || wanted === "not specified") return true;

  const normalizedKey = normalizeComparable(key).replace(/\s+/g, "");
  const structuredValue = structuredVariantValues(variant)
    .flatMap((entry) => Object.entries(entry))
    .find(([entryKey]) => normalizeComparable(entryKey).replace(/\s+/g, "") === normalizedKey)?.[1];
  if (structuredValue !== undefined) return normalizeComparable(structuredValue) === wanted;

  // CJ encode fréquemment les options uniquement dans le SKU/nom, par ex.
  // "...-Blue9668-M" pour { color: "Blue", size: "M" }. Les valeurs très
  // courtes (S/M/L) doivent matcher un token entier afin que "M" ne matche
  // jamais le M de "Men's"; les couleurs peuvent porter un suffixe CJ.
  return variantTokens(variant).some((token) =>
    token === wanted || (wanted.length >= 3 && token.startsWith(wanted))
  );
};

export function findProviderVariantBySelectedFilters(providerVariants, selectedFilters) {
  const variants = Array.isArray(providerVariants) ? providerVariants.filter(Boolean) : [];
  const filters = Object.entries(selectedFilters || {}).filter(([key, value]) =>
    !String(key).startsWith("__") && value !== null && value !== undefined && String(value).trim() !== ""
  );
  if (!variants.length || !filters.length) return null;

  const matches = variants.filter((variant) =>
    filters.every(([key, value]) => matchesSelectedFilterValue(variant, key, value))
  );
  return matches.length === 1 ? matches[0] : null;
}

export function resolveSelectedDropshippingVariant({
  product,
  selectedFilter,
  selectedFilterProduct,
  selectedVariantId,
  selectedSku,
}) {
  const variants = product?.selectedProviderVariant && typeof product.selectedProviderVariant === "object"
    ? [product.selectedProviderVariant]
    : Array.isArray(product?.providerVariants)
    ? product.providerVariants
    : product?.providerVariants && typeof product.providerVariants === "object"
      ? [product.providerVariants]
    : Array.isArray(product?.dropshippingPublic?.variants)
      ? product.dropshippingPublic.variants
      : [];
  if (!variants.length) return null;

  if (selectedVariantId) {
    const exact = variants.find((variant) => variant?.externalVariantId === selectedVariantId);
    if (exact) return exact;
  }
  if (selectedSku) {
    const exact = variants.find((variant) => variant?.sku === selectedSku);
    if (exact) return exact;
  }
  const filterProductId = selectedFilterProduct?._id || selectedFilterProduct || product?.filterPro;
  if (filterProductId) {
    const linked = variants.find((variant) => String(variant?.filterProductId) === String(filterProductId));
    if (linked) return linked;
  }
  const filterMatch = findProviderVariantBySelectedFilters(
    variants,
    selectedFilter || product?.selectedFilters || product?.filter
  );
  if (filterMatch) return filterMatch;
  return variants.length === 1 ? variants[0] : null;
}

export function prepareDropshippingOrderProduct(product) {
  const isDropshipping = product?.sourceType === "dropshipping" || Boolean(product?.providerPlatform);
  if (!isDropshipping) return { ...product, providerData: null };
  const selectedVariant = resolveSelectedDropshippingVariant({
    product,
    selectedFilter: product.selectedFilters || product.filter,
    selectedFilterProduct: product.filterPro,
    selectedVariantId: product.selectedVariantId,
    selectedSku: product.selectedVariantSku,
  });
  const variants = product?.selectedProviderVariant && typeof product.selectedProviderVariant === "object"
    ? [product.selectedProviderVariant]
    : Array.isArray(product.providerVariants)
    ? product.providerVariants
    : product.providerVariants && typeof product.providerVariants === "object"
      ? [product.providerVariants]
      : [];
  if (variants.length > 1 && (!selectedVariant || (!selectedVariant.externalVariantId && !selectedVariant.sku))) {
    throw new Error("La variante dropshipping correspondant aux filtres sélectionnés est introuvable. Veuillez sélectionner de nouveau la couleur et la taille.");
  }
  return {
    ...product,
    sourceType: "dropshipping",
    selectedFilters: product.selectedFilters || product.filter || {},
    selectedVariantId: selectedVariant?.externalVariantId || null,
    selectedVariantSku: selectedVariant?.sku || null,
    providerData: {
      provider: product.providerPlatform || {},
      variant: selectedVariant ? {
        externalVariantId: selectedVariant.externalVariantId || null,
        sku: selectedVariant.sku || null,
        name: selectedVariant.name || selectedVariant.label || null,
        image: selectedVariant.image || null,
      } : null,
      filters: product.selectedFilters || product.filter || {},
    },
  };
}
